// 16 §1.3: הגשר של בלוק app — ab.call → POST /api/runs/:runId/calls/:action עם ה-JWT של המשתמש (Bearer, לא עוגייה).
// ה-iframe לעולם לא רואה token; התשובה/השגיאה ({code, textHe, consentId?}) חוזרת לבלוק כמו שהיא. 401 → רענון אחד.
import type { AppCallMeta, AppCallResult } from '@aibify/display/client';
import { AuthExpired, getAccessToken, refresh } from './auth.ts';
import { goLogin } from './api.ts';
import { T } from './strings.ts';

const enc = encodeURIComponent;

/** app@2: o.fresh — עוקף את המטמון של פעולה עם cache («רענן»); התשובה מחזירה meta כשיש. */
export async function appCall(runId: string, action: string, args: unknown, signal: AbortSignal, retried = false, o?: { fresh?: boolean }): Promise<AppCallResult> {
  let token: string;
  try {
    token = await getAccessToken();
  } catch (e) {
    if (e instanceof AuthExpired) goLogin();
    return { ok: false, error: { code: 'denied', textHe: T.sessionExpired } };
  }
  let r: Response;
  try {
    r = await fetch(`/api/runs/${enc(runId)}/calls/${enc(action)}`, {
      method: 'POST',
      headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` },
      body: JSON.stringify({ args: args ?? {}, ...(o?.fresh ? { fresh: true } : {}) }),
      signal,
    });
  } catch {
    return { ok: false, error: { code: signal.aborted ? 'canceled' : 'unavailable', textHe: T.network } };
  }
  if (r.status === 401 && !retried) {
    try {
      await refresh();
    } catch {
      goLogin();
      return { ok: false, error: { code: 'denied', textHe: T.sessionExpired } };
    }
    return appCall(runId, action, args, signal, true, o);
  }
  const j = (await r.json().catch(() => null)) as { result?: unknown; meta?: AppCallMeta; error?: { code?: string; textHe?: string; consentId?: string } } | null;
  if (r.ok) return { ok: true, result: j?.result ?? null, ...(j?.meta ? { meta: j.meta } : {}) };
  const e = j?.error;
  return { ok: false, error: { code: e?.code ?? (r.status >= 500 ? 'unavailable' : 'failed'), textHe: e?.textHe ?? T.serverError(r.status), ...(e?.consentId ? { consentId: e.consentId } : {}) } };
}

/** POST /api/runs/:runId/warm — חימום הסשן; כשל שקט (הלחיצה תעלה קר). */
export async function warmRun(runId: string): Promise<void> {
  try {
    const token = await getAccessToken();
    await fetch(`/api/runs/${enc(runId)}/warm`, { method: 'POST', headers: { authorization: `Bearer ${token}` } });
  } catch {
    /* שקט */
  }
}

/** app@2 (ab.callMeta): POST /api/runs/:runId/calls/:action/cached — רק מהמטמון של המשתמש; אין / שגיאה → null (שקט). */
export async function appPeek(runId: string, action: string, args: unknown): Promise<{ result: unknown; meta: AppCallMeta } | null> {
  try {
    const token = await getAccessToken();
    const r = await fetch(`/api/runs/${enc(runId)}/calls/${enc(action)}/cached`, {
      method: 'POST',
      headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` },
      body: JSON.stringify({ args: args ?? {} }),
    });
    if (!r.ok) return null;
    const j = (await r.json().catch(() => null)) as { result?: unknown; meta?: AppCallMeta | null } | null;
    return j?.meta ? { result: j.result ?? null, meta: j.meta } : null;
  } catch {
    return null;
  }
}
